import React, { useState, useRef, useEffect } from "react";
import {
  X,
  Building2,
  MoreVertical,
  Users,
  User,
  Calendar,
  FileText,
  Briefcase,
  MapPin,
} from "lucide-react";
import { MdDownload } from "react-icons/md";
import { useEtatGlobal } from "../EtatGlobal.js";
import { authFetch, httpClient } from "../../HttpClient";
import { uploadFileToBlob } from "../../utils/uploadFileToBlob";
import { useAutoArchiveDocuments } from "./useAutoArchiveDocuments.js";

import { dialog } from "../../components/ui/Dialog";
import { showStatusToast } from "../../components/ui/Toast.js";
import LeadDocumentsList from "../../components/LeadDocumentsList";

function traduireStatut(statut) {
  switch (statut) {
    case "sent":
      return "Envoyée";
    case "delivered":
      return "Ouverte";
    case "completed":
      return "Terminée";
    case "declined":
      return "Refusée";
    case "voided":
      return "Annulée";
    case "signed":
      return "Signée";
    case "expired":
      return "Expirée";
    default:
      return statut;
  }
}

function getStatusBadge(status) {
  switch (status) {
    case "sent":
      return "bg-yellow-50 text-yellow-600 border-yellow-200";
    case "delivered":
      return "bg-blue-50 text-blue-600 border-blue-200";
    case "voided":
      return "bg-red-50 text-red-600 border-red-200";
    case "declined":
      return "bg-red-50 text-red-600 border-red-200";
    case "signed":
      return "bg-green-50 text-green-600 border-green-200";
    case "completed":
      return "bg-gray-100 text-gray-600 border-gray-200";
    case "expired":
      return "bg-yellow-50 text-yellow-600 border-yellow-200";
    default:
      return "bg-gray-100 text-gray-600 border-gray-200";
  }
}

function ArchiveLead({ leadData, loading, onUpdate, searchTerm }) {
  const { documents, setDocuments, fetchDocumentsLM, setLeads, utilisateur } =
    useEtatGlobal();

  // Déclenche l'archivage auto (+ 30 jours) à l'ouverture de la vue Archive,
  // puis rafraîchit la liste globale des documents une fois le backend à jour.
  const { data: autoArchiveResult } = useAutoArchiveDocuments();
  useEffect(() => {
    if (autoArchiveResult) fetchDocumentsLM();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoArchiveResult]);

  // ── Lead (Prospect) ──────────────────────────────────────────────────────
  const [selectedLead, setSelectedLead] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState({});
  const overlayRef = useRef(null);
  const inoverlayRef = useRef(null);
  const debounceRef = useRef(null);
  const pendingValuesRef = useRef({});

  const [showMenu, setShowMenu] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  // ── Lettre de mission (LM) ───────────────────────────────────────────────
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocOverlay, setShowDocOverlay] = useState(false);
  const [docShowMenu, setDocShowMenu] = useState(false);
  const [docActiveTab, setDocActiveTab] = useState("general");
  const [docOpenDetails, setDocOpenDetails] = useState({});
  const [downloading, setDownloading] = useState({});
  const [processing, setProcessing] = useState({});
  const docOverlayRef = useRef(null);
  const docInoverlayRef = useRef(null);

  // Rappels manuels envoyés pendant que ce document est ouvert, affichés
  // immédiatement dans la Timeline sans attendre le refetch de `documents`
  // (selectedDocument est un instantané figé à l'ouverture, jamais
  // resynchronisé automatiquement). Vidé à chaque ouverture d'un document
  // pour ne jamais laisser un rappel optimiste périmé s'afficher.
  const [pendingReminderEvents, setPendingReminderEvents] = useState([]);

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setFormValues({ ...lead });
    pendingValuesRef.current = { ...lead };
    setIsEditing(false);
    setShowOverlay(true);
  };

  const handleDocClick = (doc) => {
    setSelectedDocument(doc);
    setPendingReminderEvents([]);
    setDocActiveTab("general");
    setShowDocOverlay(true);
  };

  const saveInBackground = async (leadId, values) => {
    // Upload direct navigateur → Blob pour les pièces jointes (contourne la
    // limite 4,5 Mo) : un toast informe qu'un envoi de fichier — parfois
    // long selon sa taille — est en cours, sans bloquer l'overlay déjà fermé.
    let toast = null;
    try {
      const fileEntries = Object.entries(values).filter(([, val]) => val instanceof File);
      const payload = { ...values };
      fileEntries.forEach(([key]) => delete payload[key]);

      if (fileEntries.length > 0) {
        toast = showStatusToast({ status: "loading", titre: "Envoi du document...", message: "0 %" });
        for (const [key, file] of fileEntries) {
          const url = await uploadFileToBlob(
            file,
            "https://backend-myalfa.vercel.app/api/formleads/upload-token",
            ({ percentage }) =>
              toast.update({ status: "loading", titre: "Envoi du document...", message: `${Math.round(percentage)} %` })
          );
          payload[`${key}Url`] = url;
        }
      }

      const res = await authFetch(
        `https://backend-myalfa.vercel.app/api/formleads/${leadId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Erreur");
      if (toast) toast.update({ status: "success", titre: "Document envoyé", message: "" });
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Sauvegarde en arrière-plan échouée :", err);
      if (toast) toast.update({ status: "error", titre: "Échec de l'envoi", message: err.message });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const leadId = selectedLead.id;
    setFormValues((prev) => {
      const next = { ...prev, [name]: value };
      pendingValuesRef.current = next;
      return next;
    });
    setSelectedLead((prev) => ({ ...prev, [name]: value }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      saveInBackground(leadId, pendingValuesRef.current);
    }, 600);
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const leadId = selectedLead.id;
      setFormValues((prev) => {
        const next = { ...prev, [name]: files[0] };
        pendingValuesRef.current = next;
        return next;
      });
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        saveInBackground(leadId, pendingValuesRef.current);
      }, 600);
    }
  };

  const handleSave = () => {
    const leadId = selectedLead.id;
    const values = { ...formValues };

    // UI instantanée : on applique les valeurs à la carte tout de suite
    // (hors fichiers, qui n'ont pas d'URL tant que l'upload n'est pas terminé)
    const displayValues = Object.fromEntries(
      Object.entries(values).filter(([, v]) => !(v instanceof File))
    );
    setSelectedLead((prev) => ({ ...prev, ...displayValues }));
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, ...displayValues } : l))
    );
    setIsEditing(false);
    setShowOverlay(false);

    // Sauvegarde réelle en arrière-plan
    saveInBackground(leadId, values);
  };

  const handleArchive = async () => {
    const confirmArchive = await dialog.confirm(
      "Voulez-vous vraiment désarchiver ce lead ?"
    );
    if (!confirmArchive) return; // L'utilisateur a annulé

    const leadToRestore = selectedLead;
    const payload = {
      specificite: leadToRestore.lastspecificite || "contact",
    };

    // UI instantanée : la carte quitte les archives tout de suite
    setShowOverlay(false);
    setSelectedLead(null);
    setLeads((prev) =>
      prev.map((l) => (l.id === leadToRestore.id ? { ...l, ...payload } : l))
    );

    try {
      const res = await authFetch(
        `https://backend-myalfa.vercel.app/api/formleads/${leadToRestore.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Échec de l'archivage");
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
      // Échec : la carte revient dans les archives
      setLeads((prev) =>
        prev.map((l) => (l.id === leadToRestore.id ? leadToRestore : l))
      );
      dialog.alert("Impossible de désarchiver ce lead.");
    }
  };

  const handleDelete = async () => {
    const confirmDelete = await dialog.confirm(
      "Voulez-vous vraiment supprimer ce lead ? Cette action est irréversible."
    );
    if (!confirmDelete) return;

    // UI instantanée : la carte disparaît tout de suite,
    // la suppression réelle se fait en arrière-plan
    const leadToDelete = selectedLead;
    setShowOverlay(false);
    setSelectedLead(null);
    setLeads((prev) => prev.filter((l) => l.id !== leadToDelete.id));

    try {
      const res = await authFetch(
        `https://backend-myalfa.vercel.app/api/formleads/${leadToDelete.id}`,
        {
          method: "DELETE",
        }
      );

      if (!res.ok) throw new Error("Échec de la suppression");

      if (onUpdate) onUpdate(); // Pour resynchroniser la liste
    } catch (err) {
      console.error(err);
      // Échec : on remet la carte
      setLeads((prev) => [...prev, leadToDelete]);
      dialog.alert("Impossible de supprimer ce lead.");
    }
  };

  // ── Handlers LM (portés depuis ArchiveLM.js) ─────────────────────────────
  const deleteDocument = async (id) => {
    if (!(await dialog.confirm("Supprimer ce document ?"))) return;

    // UI instantanée : la carte disparaît tout de suite,
    // la suppression réelle se fait en arrière-plan
    const docToDelete = documents.find((d) => d.id === id);
    setShowDocOverlay(false);
    setSelectedDocument(null);
    setDocuments((prev) => prev.filter((d) => d.id !== id));

    try {
      await httpClient.delete(
        `https://backend-myalfa.vercel.app/api/delete-envelope/${id}`
      );
      fetchDocumentsLM();
    } catch (error) {
      console.error("Erreur suppression", error);
      // Échec : on remet la carte
      if (docToDelete) setDocuments((prev) => [...prev, docToDelete]);
      dialog.alert("Erreur lors de la suppression.");
    }
  };

  const downloadDocument = (envelopeId) => {
    const doc = documents.find((d) => d.envelopeId === envelopeId);
    setDownloading((prev) => ({ ...prev, [envelopeId]: true }));

    httpClient
      .post(
        "https://backend-myalfa.vercel.app/api/envelope/download",
        { envelopeId },
        { responseType: "blob" }
      )
      .then((response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" })
        );

        const formattedDate = new Date(doc.createdAt)
          .toLocaleDateString("fr-FR")
          .replace(/\//g, "-");
        const fileName = `${doc.emailSubject} - ${formattedDate}.pdf`;

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((error) => {
        console.error("Erreur lors du téléchargement", error);
      })
      .finally(() => {
        setDownloading((prev) => ({ ...prev, [envelopeId]: false }));
      });
  };

  const processAndSendEmail = async (envelopeId, signer) => {
    const processKey = `${envelopeId}_${signer.id}`;
    setProcessing((prev) => ({ ...prev, [processKey]: true }));

    const toast = showStatusToast({
      status: "loading",
      titre: "Envoi du rappel...",
      message: `Envoi en cours à ${signer.email}`,
    });

    try {
      if (!signer.recipientId) {
        toast.update({
          status: "error",
          titre: "Rappel non envoyé",
          message: "Erreur : recipientId manquant pour ce signataire.",
        });
        return;
      }

      // Lien statique : redirige vers DocuSign à la volée à chaque clic,
      // contrairement à /api/remind qui renvoyait une URL DocuSign à usage unique.
      const url = `https://backend-myalfa.vercel.app/api/signing-link/${envelopeId}/${signer.recipientId}`;

      await httpClient.post(
        "https://backend-myalfa.vercel.app/api/send-url-email",
        {
          email: signer.email,
          url,
          subject: "Signature requise",
          envelopeId,
        }
      );

      toast.update({
        status: "success",
        titre: "Rappel envoyé",
        message: `Email envoyé à ${signer.email}`,
      });

      // Affichage instantané dans la Timeline, en attendant que le vrai
      // historique (créé côté backend) revienne via un refetch — cet objet
      // n'est jamais persisté, juste un aperçu optimiste local.
      const auteurNom = [utilisateur?.name, utilisateur?.lastName]
        .filter(Boolean)
        .join(" ");
      setPendingReminderEvents((prev) => [
        ...prev,
        {
          createdAt: new Date().toISOString(),
          titre: "Rappel manuel de signature envoyé",
          description: auteurNom
            ? `Rappel manuel envoyé à ${signer.email} par ${auteurNom}`
            : `Rappel manuel envoyé à ${signer.email}`,
        },
      ]);

      fetchDocumentsLM();
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email", error);
      toast.update({
        status: "error",
        titre: "Échec de l'envoi",
        message: "Une erreur est survenue lors de l'envoi du rappel.",
      });
    } finally {
      setProcessing((prev) => ({ ...prev, [processKey]: false }));
    }
  };

  const handleArchiveDoc = async () => {
    const confirmArchive = await dialog.confirm(
      "Voulez-vous vraiment désarchiver ce document ?"
    );
    if (!confirmArchive) return;

    const docToRestore = selectedDocument;
    const payload = {
      specificite: docToRestore.lastspecificite,
    };

    // UI instantanée : la carte quitte les archives tout de suite
    setShowDocOverlay(false);
    setSelectedDocument(null);
    setDocuments((prev) =>
      prev.map((d) => (d.id === docToRestore.id ? { ...d, ...payload } : d))
    );

    try {
      const response = await authFetch(
        `https://backend-myalfa.vercel.app/api/update-envelope/${docToRestore.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors de l'archivage.");
      }

      fetchDocumentsLM();
    } catch (error) {
      console.error("Erreur d'archivage :", error);
      // Échec : la carte revient dans les archives
      setDocuments((prev) =>
        prev.map((d) => (d.id === docToRestore.id ? docToRestore : d))
      );
      dialog.alert("Échec du désarchivage.");
    }
  };

  // ── Fusion des deux listes archivées en une seule colonne ────────────────
  const archivedLeadCards = leadData
    .filter((lead) => lead.specificite === "archive")
    .filter((lead) =>
      `${lead.nom} ${lead.prenom}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
    .map((lead) => ({
      _kind: "lead",
      _id: `lead-${lead.id}`,
      _date: lead.updatedAt || lead.createdAt,
      raw: lead,
    }));

  const archivedDocCards = (documents || [])
    .filter((doc) => doc.specificite === "archive")
    .filter((doc) =>
      `${doc.emailSubject || ""}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
    .map((doc) => ({
      _kind: "lm",
      _id: `lm-${doc.id}`,
      _date: doc.createdAt,
      raw: doc,
    }));

  const combinedArchive = [...archivedLeadCards, ...archivedDocCards].sort(
    (a, b) => new Date(b._date) - new Date(a._date)
  );

  const autoArchivedDocCards = (documents || [])
    .filter((doc) => doc.specificite === "archive_auto")
    .filter((doc) =>
      `${doc.emailSubject || ""}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Prospects engagés : leads archivés du Kanban actif suite à l'envoi
  // d'une LM (déjà invisibles dans RDV futur/passé via status === "LM").
  const prospectsEngagesCards = leadData
    .filter((lead) => lead.status === "LM")
    .filter((lead) =>
      `${lead.nom} ${lead.prenom}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

  const envoyeDocCards = (documents || [])
    .filter((doc) => doc.specificite === "envoye")
    .filter((doc) =>
      `${doc.emailSubject || ""}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const civilite = selectedDocument?.formLead?.civilite;
  const prenom = selectedDocument?.formLead?.prenom;
  const nom = selectedDocument?.formLead?.nom;

  const isEmptyLeadDetails = [civilite, prenom, nom].every(
    (v) => !v || (typeof v === "string" && v.trim() === "")
  );

  return (
    <div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gap: "10px",
      }}
    >
      <div>
      <div className="relative flex justify-center mb-5">
        <h2 className="relative inline-flex w-[88%] items-center justify-center rounded-2xl border border-[#6e1a36] bg-[#f3edef] px-6 py-2 text-sm font-semibold tracking-wide text-[#6e1a36] shadow-md transition-all duration-300 hover:shadow-lg hover:scale-[1.02]">
          Archive
          <span className="absolute -top-2 -right-3 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#6e1a36] px-2 text-[11px] font-bold text-white shadow-md">
            {combinedArchive.length > 0 && combinedArchive.length}
          </span>
        </h2>
      </div>

      <div className="min-h-12">
        {loading ? (
          <div>
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="mx-[4%] mb-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-md"
              >
                <div className="h-3 rounded bg-slate-200 animate-pulse w-36 mb-2" />
                <div className="h-2.5 rounded bg-slate-200 animate-pulse w-24 mb-3" />
                <div className="flex items-center justify-between mt-1">
                  <div className="h-2.5 rounded bg-slate-200 animate-pulse w-28" />
                </div>
              </div>
            ))}
          </div>
        ) : combinedArchive.length === 0 ? (
          <p style={{ textAlign: "center" }}>Aucun élément archivé.</p>
        ) : (
          combinedArchive.map((item) => (
            <div
              key={item._id}
              onClick={() =>
                item._kind === "lead"
                  ? handleLeadClick(item.raw)
                  : handleDocClick(item.raw)
              }
              className="relative mx-[4%] mb-3 cursor-pointer rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-md transition-all duration-300 hover:shadow-xl hover:border-[#840040] hover:-translate-y-1"
            >
              {item._kind === "lead" ? (
                <>
                  <div className="flex items-start justify-between gap-1 w-full">
                    <p className="text-xs font-bold text-gray-800 leading-tight flex-1 min-w-0">
                      {(() => {
                        const fullName = [
                          item.raw.civilite,
                          item.raw.prenom,
                          item.raw.nom,
                        ]
                          .filter(Boolean)
                          .join(" ");
                        return fullName.length > 25
                          ? fullName.slice(0, 25) + "..."
                          : fullName;
                      })()}
                    </p>
                    <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200 leading-none">
                      Prospect
                    </span>
                  </div>
                  <span className="text-[11px] text-gray-400 font-medium tracking-wide m-0">
                    {item.raw.societeNom}
                  </span>
                  <div className="mt-1 flex items-center justify-between">
                    <p className="text-[10px] text-gray-500 font-medium">
                      {item.raw.dateRDV
                        ? new Date(item.raw.dateRDV).toLocaleDateString(
                            "fr-FR",
                            { day: "numeric", month: "long", year: "numeric" }
                          )
                        : "-"}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-1 w-full">
                    <p className="text-xs font-bold text-gray-800 leading-tight flex-1 min-w-0">
                      {(item.raw.emailSubject || "").length > 28
                        ? item.raw.emailSubject.slice(0, 28) + "..."
                        : item.raw.emailSubject}
                    </p>
                    <span
                      className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full border leading-none ${getStatusBadge(
                        item.raw.status
                      )}`}
                    >
                      LM
                    </span>
                  </div>
                  <span className="text-[11px] text-gray-400 font-medium tracking-wide m-0">
                    {traduireStatut(item.raw.status)}
                  </span>
                  <div className="mt-1 flex items-center justify-between">
                    <p className="text-[10px] text-gray-500 font-medium">
                      {new Date(item.raw.createdAt).toLocaleDateString(
                        "fr-FR",
                        { day: "numeric", month: "long", year: "numeric" }
                      )}
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadDocument(item.raw.envelopeId);
                      }}
                      disabled={downloading[item.raw.envelopeId]}
                    >
                      {downloading[item.raw.envelopeId] ? (
                        <div className="spinnerd"></div>
                      ) : (
                        <MdDownload size={15} color="#840040" />
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
      </div>

      <div>
        <div className="relative flex justify-center mb-5">
          <h2 className="relative inline-flex w-[88%] items-center justify-center rounded-2xl border border-[#6e1a36] bg-[#f3edef] px-6 py-2 text-sm font-semibold tracking-wide text-[#6e1a36] shadow-md transition-all duration-300 hover:shadow-lg hover:scale-[1.02]">
            Prospects engagés
            <span className="absolute -top-2 -right-3 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#6e1a36] px-2 text-[11px] font-bold text-white shadow-md">
              {prospectsEngagesCards.length > 0 && prospectsEngagesCards.length}
            </span>
          </h2>
        </div>
        <div className="min-h-12">
          {prospectsEngagesCards.length === 0 ? (
            <p style={{ textAlign: "center" }}>Aucun élément.</p>
          ) : (
            prospectsEngagesCards.map((lead) => (
              <div
                key={lead.id}
                onClick={() => handleLeadClick(lead)}
                className="relative mx-[4%] mb-3 cursor-pointer rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-md transition-all duration-300 hover:shadow-xl hover:border-[#840040] hover:-translate-y-1"
              >
                <div className="flex items-start justify-between gap-1 w-full">
                  <p className="text-xs font-bold text-gray-800 leading-tight flex-1 min-w-0">
                    {(() => {
                      const fullName = [lead.civilite, lead.prenom, lead.nom]
                        .filter(Boolean)
                        .join(" ");
                      return fullName.length > 25
                        ? fullName.slice(0, 25) + "..."
                        : fullName;
                    })()}
                  </p>
                  <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 leading-none">
                    LM envoyée
                  </span>
                </div>
                <span className="text-[11px] text-gray-400 font-medium tracking-wide m-0">
                  {lead.societeNom}
                </span>
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-[10px] text-gray-500 font-medium">
                    {new Date(lead.updatedAt).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div>
        <div className="relative flex justify-center mb-5">
          <h2 className="relative inline-flex w-[88%] items-center justify-center rounded-2xl border border-[#6e1a36] bg-[#f3edef] px-6 py-2 text-sm font-semibold tracking-wide text-[#6e1a36] shadow-md transition-all duration-300 hover:shadow-lg hover:scale-[1.02]">
            Archivé auto
            <span className="absolute -top-2 -right-3 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#6e1a36] px-2 text-[11px] font-bold text-white shadow-md">
              {autoArchivedDocCards.length > 0 && autoArchivedDocCards.length}
            </span>
          </h2>
        </div>
        <div className="min-h-12">
          {autoArchivedDocCards.length === 0 ? (
            <p style={{ textAlign: "center" }}>Aucun élément.</p>
          ) : (
            autoArchivedDocCards.map((doc) => (
              <div
                key={doc.id}
                onClick={() => handleDocClick(doc)}
                className="relative mx-[4%] mb-3 cursor-pointer rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-md transition-all duration-300 hover:shadow-xl hover:border-[#840040] hover:-translate-y-1"
              >
                <div className="flex items-start justify-between gap-1 w-full">
                  <p className="text-xs font-bold text-gray-800 leading-tight flex-1 min-w-0">
                    {(doc.emailSubject || "").length > 28
                      ? doc.emailSubject.slice(0, 28) + "..."
                      : doc.emailSubject}
                  </p>
                  <span
                    className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full border leading-none ${getStatusBadge(
                      doc.status
                    )}`}
                  >
                    LM
                  </span>
                </div>
                <span className="text-[11px] text-gray-400 font-medium tracking-wide m-0">
                  {traduireStatut(doc.status)}
                </span>
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-[10px] text-gray-500 font-medium">
                    {new Date(doc.createdAt).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadDocument(doc.envelopeId);
                    }}
                    disabled={downloading[doc.envelopeId]}
                  >
                    {downloading[doc.envelopeId] ? (
                      <div className="spinnerd"></div>
                    ) : (
                      <MdDownload size={15} color="#840040" />
                    )}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div>
        <div className="relative flex justify-center mb-5">
          <h2 className="relative inline-flex w-[88%] items-center justify-center rounded-2xl border border-[#6e1a36] bg-[#f3edef] px-6 py-2 text-sm font-semibold tracking-wide text-[#6e1a36] shadow-md transition-all duration-300 hover:shadow-lg hover:scale-[1.02]">
            Envoyé
            <span className="absolute -top-2 -right-3 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#6e1a36] px-2 text-[11px] font-bold text-white shadow-md">
              {envoyeDocCards.length > 0 && envoyeDocCards.length}
            </span>
          </h2>
        </div>
        <div className="min-h-12">
          {envoyeDocCards.length === 0 ? (
            <p style={{ textAlign: "center" }}>Aucun élément.</p>
          ) : (
            envoyeDocCards.map((doc) => (
              <div
                key={doc.id}
                onClick={() => handleDocClick(doc)}
                className="relative mx-[4%] mb-3 cursor-pointer rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-md transition-all duration-300 hover:shadow-xl hover:border-[#840040] hover:-translate-y-1"
              >
                <div className="flex items-start justify-between gap-1 w-full">
                  <p className="text-xs font-bold text-gray-800 leading-tight flex-1 min-w-0">
                    {(doc.emailSubject || "").length > 28
                      ? doc.emailSubject.slice(0, 28) + "..."
                      : doc.emailSubject}
                  </p>
                  <span
                    className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full border leading-none ${getStatusBadge(
                      doc.status
                    )}`}
                  >
                    LM
                  </span>
                </div>
                <span className="text-[11px] text-gray-400 font-medium tracking-wide m-0">
                  {traduireStatut(doc.status)}
                </span>
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-[10px] text-gray-500 font-medium">
                    {new Date(doc.createdAt).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadDocument(doc.envelopeId);
                    }}
                    disabled={downloading[doc.envelopeId]}
                  >
                    {downloading[doc.envelopeId] ? (
                      <div className="spinnerd"></div>
                    ) : (
                      <MdDownload size={15} color="#840040" />
                    )}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      </div>

      {/* Overlay Prospect (Lead) */}
      {showOverlay && selectedLead && (
        <div className="overlayAC" ref={overlayRef}>
          <div className="inoverlayLead" ref={inoverlayRef}>
            <button
              className="close-button"
              onClick={() => setShowOverlay(false)}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "transparent",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                zIndex: 10000,
              }}
            >
              <button
                aria-label="Fermer"
                className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </button>

            {/* Vue Détails */}
            {!isEditing ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  padding: "0 5%",
                  boxSizing: "border-box",
                }}
              >
                <div className="flex  justify-between mb-6 items-center">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center">
                      <Building2 className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <div className="text-1xl font-bold text-slate-900">
                        {selectedLead.prenom} {selectedLead.nom}{" "}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center gap-2">
                          <div
                            className={`px-2 py-1 text-xs font-semibold rounded-md border bg-yellow-100 text-yellow-800 border-yellow-300`}
                          >
                            Prospect
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mr-10">
                    <div
                      className={`cursor-pointer flex items-center  h-8 px-3  rounded-md font-semibold text-sm bg-white text-black border border-slate-300 hover:bg-slate-50`}
                      onClick={() => setIsEditing(true)}
                    >
                      <p>Modifier</p>
                    </div>
                    <div className="relative">
                      <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-2 rounded-md hover:bg-gray-100"
                      >
                        <MoreVertical className="w-5 h-5 text-gray-600" />
                      </button>

                      {showMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                          <div
                            className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleDelete();
                              setShowMenu(false);
                            }}
                          >
                            Supprimer
                          </div>
                          <div
                            className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleArchive();
                              setShowMenu(false);
                            }}
                          >
                            Désarchiver
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-blue-700">
                        Missions en cours
                      </span>
                      <div className="bg-purple-600 text-white text-xs py-1 px-1.5 font-semibold rounded-md">
                        {selectedLead.creationSct?.filter(
                          (todo) =>
                            todo.status !== "Terminer" &&
                            todo.status !== "Terminé"
                        ).length || 0}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-purple-700">
                        Tâches en cours
                      </span>

                      <div className="bg-[#8B1538] text-white text-xs py-1 px-1.5 font-semibold rounded-md">
                        {selectedLead.ToDo?.filter(
                          (todo) =>
                            todo.status === "En cours" &&
                            Array.isArray(todo.users) &&
                            todo.users.length > 0
                        ).length || 0}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid w-full grid-cols-2 bg-slate-100 p-1 h-auto rounded-md mb-6">
                  {[
                    { value: "general", label: "Général" },

                    { value: "timeline", label: "Timeline" },
                  ].map((tab) => (
                    <button
                      key={tab.value}
                      onClick={() => setActiveTab(tab.value)}
                      className={`py-2 text-xs rounded-md transition-colors font-medium
              ${
                activeTab === tab.value
                  ? "bg-white"
                  : "bg-transparent hover:bg-slate-200"
              }
            `}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div>
                  {activeTab === "general" && (
                    <>
                      <div className="bg-white rounded-lg p-4 border border-slate-200 mb-5">
                        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-[#9B1B5D]" />
                          Information de Rendez-vous
                        </h3>

                        <div className="space-y-3 text-sm">
                          <div className="items-start justify-between">
                            <span className="text-slate-500">
                              Objet du Rendez-vous :{" "}
                            </span>
                            <span className="font-medium text-slate-900 text-right">
                              {selectedLead.objetRDV || "-"}
                            </span>
                          </div>

                          {selectedLead.user && (
                            <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                              <div className="flex items-center gap-2 text-xs text-slate-600">
                                <Calendar className="w-4 h-4 text-[#9B1B5D]" />
                                <span>
                                  Le{" "}
                                  {new Date(
                                    selectedLead.dateRDV
                                  ).toLocaleDateString("fr-FR", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  })}{" "}
                                  à {selectedLead.heureRDV}
                                </span>
                              </div>

                              <div className="flex items-center gap-2 text-xs text-slate-600">
                                <MapPin className="w-4 h-4 text-[#9B1B5D]" />
                                <span>{selectedLead.lieuRDV || "-"}</span>
                              </div>

                              <div className="flex items-center gap-2 text-xs text-slate-600">
                                <User className="w-4 h-4 text-[#9B1B5D]" />
                                <span>
                                  {selectedLead.user.name}{" "}
                                  {selectedLead.user.lastName}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Information client */}
                        <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-[#9B1B5D]" />
                            Information client
                          </h3>
                          <div className="space-y-2">
                            {/* Infos générales */}
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Civilité</span>
                              <span className="font-medium text-slate-900">
                                {selectedLead.civilite || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Nom</span>
                              <span className="font-medium text-slate-900">
                                {selectedLead.nom || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Prenom</span>
                              <span className="font-medium text-slate-900">
                                {selectedLead.prenom || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Adresse</span>
                              <span className="font-medium text-slate-900">
                                {selectedLead.adresse || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">
                                Telephone
                              </span>
                              <span className="font-medium text-slate-900">
                                {selectedLead.tel || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Email</span>
                              <span className="font-medium text-slate-900">
                                {selectedLead.mail || "-"}
                              </span>
                            </div>

                            {/* Contact représentant */}
                            <div className="bg-slate-50 rounded-lg p-2 mt-2 space-y-1.5">
                              <p className="text-xs font-semibold text-slate-700 mb-1">
                                Structure existante
                              </p>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">
                                  Forme Social
                                </span>
                                <span className="font-medium text-slate-900">
                                  {selectedLead.formeSociale || "-"} &nbsp;
                                  {selectedLead.sociale}{" "}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">
                                  Dénomination Social
                                </span>
                                <span className="font-medium text-slate-900">
                                  {selectedLead.societeNom || "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Équipe Cabinet */}
                        <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Users className="w-4 h-4 text-[#9B1B5D]" />
                            Équipe Cabinet
                          </h3>

                          <div className="space-y-2.5">
                            {/* Expert comptable */}
                            <div className="flex items-center gap-2 p-2 bg-[#9B1B5D]/5 rounded-lg">
                              <div className="w-8 h-8 rounded-full bg-[#9B1B5D] flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-slate-500">
                                  Collaborateur
                                </p>
                                <p className="text-xs font-semibold text-slate-900 truncate">
                                  {" "}
                                  {selectedLead.user?.name}{" "}
                                  {selectedLead.user?.lastName}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-5">
                        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#9B1B5D]" />
                          Documents liés
                        </h3>

                        <LeadDocumentsList lead={selectedLead} />
                      </div>
                      <br />
                    </>
                  )}
                  {activeTab === "timeline" && (
                    <div className="bg-white border border-slate-200 rounded-lg p-4 mt-0">
                      {/* Header */}
                      <div className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-900 mb-4">
                        <div className="w-2 h-2 rounded-full bg-[#8B1538]"></div>
                        Timeline
                      </div>

                      {/* Création LEAD */}
                      <div
                        style={{
                          position: "relative",
                          paddingLeft: "20px",
                          marginBottom: "15px",
                          borderLeft: "2px solid #cbd5e1",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            left: "-6px",
                            top: "6px",
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            backgroundColor: "#2563eb",
                            border: "2px solid #fff",
                          }}
                        ></div>

                        <div
                          style={{
                            background: "#eff6ff",
                            borderRadius: "8px",
                            padding: "10px",
                            border: "1px solid #cbd5e1",
                          }}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <p className="text-[13px] font-semibold text-blue-800">
                              Création de la fiche LEAD
                            </p>
                            <span className="text-xs text-blue-700">
                              {new Date(
                                selectedLead.createdAt
                              ).toLocaleDateString("fr-FR", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })}
                            </span>
                          </div>

                          <p className="text-[13px] text-blue-900">
                            Création de la fiche LEAD avec{" "}
                            {selectedLead.specificite}
                          </p>
                        </div>
                      </div>

                      {/* Historique dynamique */}
                      {selectedLead.HistoriqueFormLEAD &&
                        selectedLead.HistoriqueFormLEAD.length > 0 &&
                        selectedLead.HistoriqueFormLEAD.map((item) => (
                          <div
                            key={item.id}
                            style={{
                              position: "relative",
                              paddingLeft: "20px",
                              marginBottom: "15px",
                              borderLeft: "2px solid #cbd5e1",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                left: "-6px",
                                top: "6px",
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                backgroundColor: "#2563eb",
                                border: "2px solid #fff",
                              }}
                            ></div>

                            <div
                              style={{
                                background: "#eff6ff",
                                borderRadius: "8px",
                                padding: "10px",
                                border: "1px solid #cbd5e1",
                              }}
                            >
                              <div className="flex justify-between items-start mb-1">
                                <p className="text-[13px] font-semibold text-blue-800">
                                  {item.titre}
                                </p>
                                <span className="text-xs text-blue-700">
                                  {new Date(
                                    item.createdAt
                                  ).toLocaleDateString("fr-FR", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  })}
                                </span>
                              </div>

                              {item.titre ===
                                "Formulaire de Rendez-vous renseigné" && (
                                <span
                                  onClick={() =>
                                    setDocOpenDetails((prev) => ({
                                      ...prev,
                                      [item.id]: !prev[item.id],
                                    }))
                                  }
                                  style={{
                                    color: "#1e40af",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {docOpenDetails[item.id]
                                    ? "masquer détails"
                                    : "voir détails"}
                                </span>
                              )}

                              {docOpenDetails[item.id] &&
                                item.titre ===
                                  "Formulaire de Rendez-vous renseigné" && (
                                  <div
                                    style={{
                                      marginTop: "10px",
                                      background: "#ffffff",
                                      borderRadius: "8px",
                                      padding: "10px",
                                      border: "1px solid #cbd5e1",
                                    }}
                                  >
                                    <h4 className="text-sm font-semibold text-blue-800 mb-3">
                                      Détails Formulaire rempli
                                    </h4>

                                    <table
                                      style={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                      }}
                                    >
                                      <tbody>
                                        {[
                                          [
                                            "Nom complet",
                                            selectedLead.prospect_nomComplet,
                                          ],
                                          [
                                            "Email",
                                            selectedLead.prospect_email,
                                          ],
                                          [
                                            "Téléphone",
                                            selectedLead.prospect_telephone,
                                          ],
                                          [
                                            "Adresse",
                                            selectedLead.prospect_adresse,
                                          ],
                                          [
                                            "Type de prospect",
                                            selectedLead.prospect_prospectType,
                                          ],
                                          [
                                            "Description du projet",
                                            selectedLead.prospect_descriptionProjet,
                                          ],
                                          [
                                            "Nombre d'associés",
                                            selectedLead.prospect_nombreAssocies,
                                          ],
                                          [
                                            "Forme sociale",
                                            selectedLead.prospect_formSociale,
                                          ],
                                          [
                                            "Numéro SIRET",
                                            selectedLead.prospect_numSiret,
                                          ],
                                          [
                                            "Nom de la société",
                                            selectedLead.prospect_nomSociete,
                                          ],
                                          [
                                            "Adresse société",
                                            selectedLead.prospect_adresseSociete,
                                          ],
                                          [
                                            "Date de création société",
                                            selectedLead.prospect_dateCreationSociete,
                                          ],
                                          [
                                            "Description du besoin",
                                            selectedLead.prospect_descriptionBesoin,
                                          ],
                                          [
                                            "Service sélectionné",
                                            selectedLead.prospect_selectedService,
                                          ],
                                        ].map(([label, value]) => (
                                          <tr key={label}>
                                            <td
                                              style={{
                                                fontWeight: "600",
                                                padding: "5px 10px",
                                                width: "35%",
                                                color: "#1e3a8a",
                                              }}
                                            >
                                              {label} :
                                            </td>
                                            <td
                                              style={{
                                                padding: "5px 10px",
                                                color: "#1e3a8a",
                                              }}
                                            >
                                              {value ?? " "}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Vue Édition */
              <div className="FormLead">
                <h2>Modifier le Lead</h2>
                <hr style={{ margin: "10px 0" }} />
                <br />
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSave();
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "25px",
                      marginBottom: "10px",
                    }}
                  >
                    <select
                      name="civilite"
                      value={formValues.civilite || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Civilité</option>
                      <option value="M.">Monsieur</option>
                      <option value="Mme.">Madame</option>
                    </select>
                    <input
                      name="nom"
                      style={{ width: "100%" }}
                      placeholder="Nom"
                      value={formValues.nom || ""}
                      onChange={handleChange}
                    />
                    <input
                      name="prenom"
                      style={{ width: "100%" }}
                      placeholder="Prénom"
                      value={formValues.prenom || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "25px",
                    }}
                  >
                    <input
                      style={{ width: "100%" }}
                      type="text"
                      name="tel"
                      placeholder="Téléphone"
                      value={formValues.tel || ""}
                      onChange={handleChange}
                    />
                    <input
                      style={{ width: "100%" }}
                      type="email"
                      name="mail"
                      placeholder="Email"
                      value={formValues.mail || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      marginTop: "15px",
                      alignItems: "center",
                      justifyContent: "center",
                      display: "flex",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    <input
                      style={{ width: "100%" }}
                      name="adresse"
                      placeholder="Adresse"
                      value={formValues.adresse || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <hr style={{ margin: "25px 0" }} />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "25px",
                      marginTop: "15px",
                    }}
                  >
                    <select
                      name="formeSociale"
                      value={formValues.formeSociale || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Forme Sociale</option>
                      {[
                        "E.I",
                        "E.I.R.L",
                        "EARL",
                        "EURL",
                        "SARL",
                        "SASU",
                        "SAS",
                        "SCI",
                        "SCCV",
                        "LMNP",
                        "GFA",
                        "SC",
                        "SCP",
                        "SEL",
                        "N/A",
                      ].map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>

                    <input
                      style={{ width: "100%" }}
                      type="text"
                      name="societeNom"
                      placeholder="Dénomination Sociale"
                      value={formValues.societeNom || ""}
                      onChange={handleChange}
                    />
                    <input
                      style={{ width: "100%" }}
                      type="text"
                      name="tel"
                      placeholder="SIREN société"
                      value={formValues.sirenSociete || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <hr style={{ margin: "25px 0" }} />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "25px",
                      marginTop: "15px",
                    }}
                  >
                    <input
                      style={{ width: "100%" }}
                      type="date"
                      name="dateRDV"
                      value={formValues.dateRDV?.split("T")[0] || ""}
                      onChange={handleChange}
                    />
                    <input
                      style={{ width: "100%" }}
                      type="time"
                      name="heureRDV"
                      value={formValues.heureRDV || ""}
                      onChange={handleChange}
                    />
                    <select
                      style={{ width: "100%" }}
                      name="lieuRDV"
                      value={formValues.lieuRDV || ""}
                      onChange={handleChange}
                    >
                      <option value="">— Site —</option>
                      <option value="27 rue Vallon Hoarau, LE TAMPON (97430)">
                        27 rue Vallon Hoarau, LE TAMPON (97430)
                      </option>
                      <option value="19 chemin du Château d'Eau, Espace Turquoise n° 23, SAINT-PIERRE (97410)">
                        19 chemin du Château d'Eau, Espace Turquoise n° 23,
                        SAINT-PIERRE (97410)
                      </option>
                      <option value="RDV en ligne">RDV en ligne</option>
                      <option value="Rendez-vous téléphonique">Rendez-vous téléphonique</option>
                    </select>
                  </div>
                  {formValues.lieuRDV === "RDV en ligne" && (
                    <div style={{ marginTop: "10px" }}>
                      <span>Lien Google Meet :</span>
                      <input
                        style={{ width: "100%", marginTop: "5px" }}
                        type="url"
                        name="lienRDV"
                        value={formValues.lienRDV || ""}
                        onChange={handleChange}
                        placeholder="https://meet.google.com/..."
                      />
                    </div>
                  )}
                  <hr style={{ margin: "25px 0" }} />
                  {/* Champs fichiers */}
                  {[
                    { key: "pieceIdentite", label: "Pièce d'identité" },
                    { key: "bilanDocument", label: "Bilan" },
                    {
                      key: "identityDocument",
                      label: "Justificatif d'identité",
                    },
                    { key: "kbisDocument", label: "KBIS" },
                    { key: "statusDocument", label: "Statuts" },
                  ].map((doc) => (
                    <div key={doc.key}>
                      <span>{doc.label} :</span>
                      <br />
                      <input
                        type="file"
                        name={doc.key}
                        onChange={handleFileChange}
                      />
                      <br />
                      <br />
                    </div>
                  ))}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "30px",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      style={{
                        backgroundColor: "#840040",
                        color: "#fff",
                        padding: "10px 30px",
                        borderRadius: "5px",
                        border: "none",
                      }}
                      type="submit"
                    >
                      Enregistrer
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      style={{
                        backgroundColor: "#840040",
                        color: "#fff",
                        padding: "10px 30px",
                        borderRadius: "5px",
                        border: "none",
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </form>
                <br />
                <br />
                <br />
                <br />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overlay Lettre de mission (LM) */}
      {showDocOverlay && selectedDocument && (
        <div className="overlayAC" ref={docOverlayRef}>
          <div className="inoverlayLead" ref={docInoverlayRef}>
            <button
              className="close-button"
              onClick={(e) => {
                e.stopPropagation();
                setShowDocOverlay(false);
              }}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "transparent",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                zIndex: 10000,
              }}
            >
              <button
                aria-label="Fermer"
                className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </button>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                padding: "0 5%",
                boxSizing: "border-box",
              }}
            >
              <div className="flex  justify-between mb-6 items-center">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="text-1xl font-bold text-slate-900 m-0">
                      {selectedDocument.emailSubject.split(" - ")[0]}
                    </div>
                    <div className="text-1xl font-bold text-slate-400 m-0">
                      {selectedDocument.emailSubject.split(" - ")[1]}
                    </div>

                    <span
                      className={`px-2 py-[1px] rounded-full text-[7px] font-medium border ${
                        (new Date() - new Date(selectedDocument.createdAt)) /
                          (1000 * 60 * 60 * 24) <=
                        7
                          ? "bg-yellow-50 text-red-800 border-red-300"
                          : getStatusBadge(selectedDocument.status)
                      }`}
                    >
                      {(new Date() - new Date(selectedDocument.createdAt)) /
                        (1000 * 60 * 60 * 24) <=
                      7
                        ? "Relancer"
                        : traduireStatut(selectedDocument.status)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 mr-10">
                  <div className="relative">
                    <button
                      onClick={() => setDocShowMenu(!docShowMenu)}
                      className="p-2 rounded-md hover:bg-gray-100"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-600" />
                    </button>

                    {docShowMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                        <div
                          className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            handleArchiveDoc();
                            setDocShowMenu(false);
                          }}
                        >
                          Désarchiver
                        </div>

                        <div
                          className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            deleteDocument(selectedDocument.id);
                            setDocShowMenu(false);
                          }}
                        >
                          Supprimer
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid w-full grid-cols-2 bg-slate-100 p-1 h-auto rounded-md mb-6">
                {[
                  { value: "general", label: "Général" },

                  { value: "timeline", label: "Timeline" },
                ].map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => setDocActiveTab(tab.value)}
                    className={`py-2 text-xs rounded-md transition-colors font-medium
              ${
                docActiveTab === tab.value
                  ? "bg-white"
                  : "bg-transparent hover:bg-slate-200"
              }
            `}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div>
                {docActiveTab === "general" && (
                  <>
                    <div className="details-view" style={{ width: "100%" }}>
                      {isEmptyLeadDetails ? (
                        <p
                          style={{
                            color: "gray",
                            fontStyle: "italic",
                            marginTop: "10px",
                          }}
                        ></p>
                      ) : (
                        <>
                          <div className="bg-white rounded-lg p-4 border border-slate-200 mb-5">
                            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                              <Briefcase className="w-4 h-4 text-[#9B1B5D]" />
                              Information de Rendez-vous
                            </h3>
                            <div className="space-y-3 text-sm">
                              <div className="items-start justify-between">
                                <span className="text-slate-500">
                                  Objet du Rendez-vous :{" "}
                                </span>
                                <span className="font-medium text-slate-900 text-right">
                                  {selectedDocument?.formLead?.objetRDV}
                                </span>
                              </div>

                              {selectedDocument?.formLead?.user && (
                                <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                                  <div className="flex items-center gap-2 text-xs text-slate-600">
                                    <Calendar className="w-4 h-4 text-[#9B1B5D]" />
                                    <span>
                                      Le{" "}
                                      {new Date(
                                        selectedDocument?.formLead?.dateRDV
                                      ).toLocaleDateString("fr-FR", {
                                        day: "numeric",
                                        month: "long",
                                        year: "numeric",
                                      })}{" "}
                                      à{" "}
                                      {selectedDocument?.formLead?.heureRDV}{" "}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-2 text-xs text-slate-600">
                                    <MapPin className="w-4 h-4 text-[#9B1B5D]" />
                                    <span>
                                      {" "}
                                      {selectedDocument?.formLead?.lieuRDV}{" "}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-2 text-xs text-slate-600">
                                    <User className="w-4 h-4 text-[#9B1B5D]" />
                                    <span>
                                      {selectedDocument?.formLead?.user.name}{" "}
                                      {
                                        selectedDocument?.formLead?.user
                                          .lastName
                                      }
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Information client */}
                        <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-[#9B1B5D]" />
                            Information client
                          </h3>
                          <div className="space-y-2">
                            {/* Infos générales */}
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Civilité</span>
                              <span className="font-medium text-slate-900">
                                {selectedDocument.civilite_lm || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Nom</span>
                              <span className="font-medium text-slate-900">
                                {selectedDocument.nom || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Prenom</span>
                              <span className="font-medium text-slate-900">
                                {selectedDocument.prenom || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Adresse</span>
                              <span className="font-medium text-slate-900">
                                {selectedDocument.adresse || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">
                                Telephone
                              </span>
                              <span className="font-medium text-slate-900">
                                {selectedDocument.telephone || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Email</span>
                              <span className="font-medium text-slate-900">
                                {selectedDocument.email || "-"}
                              </span>
                            </div>

                            {/* Contact représentant */}
                            <div className="bg-slate-50 rounded-lg p-2 mt-2 space-y-1.5">
                              <p className="text-xs font-semibold text-slate-700 mb-1">
                                Structure existante
                              </p>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">
                                  Forme Social
                                </span>
                                <span className="font-medium text-slate-900">
                                  {selectedDocument.formeSociale || "-"}{" "}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">
                                  Dénomination Social
                                </span>
                                <span className="font-medium text-slate-900">
                                  {selectedDocument.societeNom || "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Équipe Cabinet */}
                        <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Users className="w-4 h-4 text-[#9B1B5D]" />
                            Équipe Cabinet
                          </h3>

                          <div className="space-y-2.5">
                            {/* Expert comptable */}
                            <div className="flex items-center gap-2 p-2 bg-[#9B1B5D]/5 rounded-lg">
                              <div className="w-8 h-8 rounded-full bg-[#9B1B5D] flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-slate-500">
                                  Signataire
                                </p>
                                <p className="text-xs font-semibold text-slate-900 truncate">
                                  {selectedDocument?.signers?.find(
                                    (s) =>
                                      s.name === "Mrs Samuel" ||
                                      s.name === "Mrs Anthony"
                                  )?.name || "—"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-5">
                        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#9B1B5D]" />
                          Documents liés
                        </h3>
                        <LeadDocumentsList lead={selectedDocument?.formLead} />
                      </div>
                      <div className="space-y-3 mt-5">
                        {selectedDocument.signers.map((signer) => {
                          const processKey = `${selectedDocument.envelopeId}_${signer.id}`;
                          return (
                            <div
                              key={signer.id}
                              className="bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between"
                            >
                              {/* Info signer */}
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-[#9B1B5D]/10 flex items-center justify-center flex-shrink-0">
                                  <User className="w-4 h-4 text-[#9B1B5D]" />
                                </div>
                                <div className="flex flex-col text-xs">
                                  <span className="font-medium text-slate-900 truncate">
                                    {signer.name}
                                  </span>
                                </div>
                                <div className="flex flex-col text-xs ml-4">
                                  <span className="font-medium text-slate-900 truncate">
                                    {signer.email}
                                  </span>
                                </div>
                              </div>

                              {/* Bouton rappel */}
                              <button
                                onClick={() =>
                                  processAndSendEmail(
                                    selectedDocument.envelopeId,
                                    signer
                                  )
                                }
                                disabled={processing[processKey]}
                                className={`text-xs font-medium px-3 py-1 rounded-sm ${
                                  processing[processKey]
                                    ? "bg-[#9B1B5D]/50 cursor-not-allowed"
                                    : "bg-[#9B1B5D] hover:bg-[#840040]"
                                } text-white`}
                              >
                                {processing[processKey] ? (
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  "Renvoyer un Mail de rappel"
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>

                      {/* Téléchargement du document */}
                      <div className="flex justify-end mt-5">
                        <button
                          onClick={() =>
                            downloadDocument(selectedDocument.envelopeId)
                          }
                          disabled={downloading[selectedDocument.envelopeId]}
                          className="flex items-center gap-2 text-xs font-medium px-4 py-2 rounded-md bg-white border border-slate-200 hover:bg-slate-50 transition"
                        >
                          {downloading[selectedDocument.envelopeId] ? (
                            <div className="spinnerd"></div>
                          ) : (
                            <MdDownload size={16} color="#840040" />
                          )}
                          Télécharger le document
                        </button>
                      </div>
                    </div>
                  </>
                )}
                {docActiveTab === "timeline" && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4 mt-0">
                    {/* Header */}
                    <div className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-900 mb-4">
                      <div className="w-2 h-2 rounded-full bg-[#8B1538]"></div>
                      Timeline
                    </div>

                    {/* Tri des événements du plus récent au moins récent */}
                    {[
                      {
                        date: selectedDocument?.formLead?.createdAt,
                        titre: "Création de la fiche LEAD",
                        description: `Création de la fiche LEAD avec ${selectedDocument?.formLead?.specificite}`,
                        data: null,
                      },
                      {
                        date: selectedDocument?.createdAt,
                        titre: "Création du LM",
                        description: null,
                        data: null,
                      },
                      // Historique Envelope
                      ...(selectedDocument?.historiqueEnvelope || []).map(
                        (item) => ({
                          date: item.createdAt,
                          titre: item.titre,
                          description: item.description,
                          data: item,
                        })
                      ),
                      // Historique du FormLead
                      ...(
                        selectedDocument?.formLead?.HistoriqueFormLEAD || []
                      ).map((item) => ({
                        date: item.createdAt,
                        titre: item.titre,
                        description: item.description,
                        data: item,
                      })),
                      ...pendingReminderEvents.map((item) => ({
                        date: item.createdAt,
                        titre: item.titre,
                        description: item.description,
                        data: null,
                      })),
                    ]
                      .filter((event) => event.date)
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((event, index) => {
                        // Rappels de signature (auto = relance J-7 système,
                        // manuel = bouton "Renvoyer un Mail de rappel") :
                        // distingués visuellement du reste de la timeline.
                        const isRappel = event.titre?.startsWith("Rappel");
                        const isRappelAuto = event.titre?.includes(
                          "automatique",
                        );
                        const pointColor = isRappel
                          ? isRappelAuto
                            ? "#f59e0b"
                            : "#ea580c"
                          : "#2563eb";
                        const cardBg = isRappel ? "#fff7ed" : "#eff6ff";
                        const cardBorder = isRappel ? "#fed7aa" : "#cbd5e1";
                        const titleColor = isRappel ? "#9a3412" : "#1e40af";

                        return (
                        <div
                          key={index}
                          style={{
                            position: "relative",
                            paddingLeft: "20px",
                            marginBottom: "15px",
                            borderLeft: "2px solid #cbd5e1",
                          }}
                        >
                          {/* Point timeline */}
                          <div
                            style={{
                              position: "absolute",
                              left: "-6px",
                              top: "6px",
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              backgroundColor: pointColor,
                              border: "2px solid #fff",
                            }}
                          ></div>

                          {/* Card */}
                          <div
                            style={{
                              background: cardBg,
                              borderRadius: "8px",
                              padding: "10px",
                              border: `1px solid ${cardBorder}`,
                            }}
                          >
                            <div className="flex justify-between items-start mb-1">
                              <p
                                className="text-[13px] font-semibold"
                                style={{ color: titleColor }}
                              >
                                {isRappel && "🔔 "}
                                {event.titre}
                                {isRappel && (
                                  <span
                                    className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                                    style={{
                                      background: isRappelAuto
                                        ? "#fef3c7"
                                        : "#ffedd5",
                                      color: isRappelAuto
                                        ? "#92400e"
                                        : "#9a3412",
                                    }}
                                  >
                                    {isRappelAuto ? "Système" : "Manuel"}
                                  </span>
                                )}
                              </p>
                              <span className="text-xs" style={{ color: titleColor }}>
                                {new Date(event.date).toLocaleDateString(
                                  "fr-FR",
                                  {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  }
                                )}
                              </span>
                            </div>

                            {event.description && (
                              <p className="text-[13px] text-blue-900">
                                {event.description}
                              </p>
                            )}

                            {/* Détails formulaire si nécessaire */}
                            {event.data &&
                              event.data.titre ===
                                "Formulaire de Rendez-vous renseigné" && (
                                <>
                                  <span
                                    onClick={() =>
                                      setDocOpenDetails((prev) => ({
                                        ...prev,
                                        [event.data.id]:
                                          !prev[event.data.id],
                                      }))
                                    }
                                    style={{
                                      color: "#1e40af",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    {docOpenDetails[event.data.id]
                                      ? "masquer détails"
                                      : "voir détails"}
                                  </span>

                                  {docOpenDetails[event.data.id] && (
                                    <div
                                      style={{
                                        marginTop: "10px",
                                        background: "#ffffff",
                                        borderRadius: "8px",
                                        padding: "10px",
                                        border: "1px solid #cbd5e1",
                                      }}
                                    >
                                      <h4 className="text-sm font-semibold text-blue-800 mb-3">
                                        Détails Formulaire rempli
                                      </h4>

                                      <table
                                        style={{
                                          width: "100%",
                                          borderCollapse: "collapse",
                                        }}
                                      >
                                        <tbody>
                                          {[
                                            [
                                              "Nom complet",
                                              event.data.prospect_nomComplet,
                                            ],
                                            [
                                              "Email",
                                              event.data.prospect_email,
                                            ],
                                            [
                                              "Téléphone",
                                              event.data.prospect_telephone,
                                            ],
                                            [
                                              "Adresse",
                                              event.data.prospect_adresse,
                                            ],
                                            [
                                              "Type de prospect",
                                              event.data
                                                .prospect_prospectType,
                                            ],
                                            [
                                              "Description du projet",
                                              event.data
                                                .prospect_descriptionProjet,
                                            ],
                                            [
                                              "Nombre d'associés",
                                              event.data
                                                .prospect_nombreAssocies,
                                            ],
                                            [
                                              "Forme sociale",
                                              event.data.prospect_formSociale,
                                            ],
                                            [
                                              "Numéro SIRET",
                                              event.data.prospect_numSiret,
                                            ],
                                            [
                                              "Nom de la société",
                                              event.data.prospect_nomSociete,
                                            ],
                                            [
                                              "Adresse société",
                                              event.data
                                                .prospect_adresseSociete,
                                            ],
                                            [
                                              "Date de création société",
                                              event.data
                                                .prospect_dateCreationSociete,
                                            ],
                                            [
                                              "Description du besoin",
                                              event.data
                                                .prospect_descriptionBesoin,
                                            ],
                                            [
                                              "Service sélectionné",
                                              event.data
                                                .prospect_selectedService,
                                            ],
                                          ].map(([label, value]) => (
                                            <tr key={label}>
                                              <td
                                                style={{
                                                  fontWeight: "600",
                                                  padding: "5px 10px",
                                                  width: "35%",
                                                  color: "#1e3a8a",
                                                }}
                                              >
                                                {label} :
                                              </td>
                                              <td
                                                style={{
                                                  padding: "5px 10px",
                                                  color: "#1e3a8a",
                                                }}
                                              >
                                                {value ?? " "}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </>
                              )}
                          </div>
                        </div>
                        );
                      })}
                  </div>
                )}
              </div>
              <br />
              <br />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ArchiveLead;
